<template>
  <div class="w-full h-full group">
    <div
      v-if="delete_available"
      class="absolute z-40 flex items-center justify-center w-5 h-5 p-1 text-xs text-white transition-all duration-500 ease-in-out bg-red-500 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer hover:scale-150 right-2 top-1"
      @click.stop="events.deleteClick(id)">
      <X color="white" class="w-full h-full p-0.5" />
    </div>

    <Handle id="t" type="source" position="top" :style="sourceHandleStyleT" />
    <Handle id="b" type="source" position="bottom" :style="sourceHandleStyleB" />
    <Handle id="r" type="source" position="right" :style="sourceHandleStyleR" />
    <Handle id="l" type="source" position="left" :style="sourceHandleStyleL" />

    <Handle id="t" type="target" position="top" :style="sourceHandleStyleT" />
    <Handle id="b" type="target" position="bottom" :style="sourceHandleStyleB" />
    <Handle id="r" type="target" position="right" :style="sourceHandleStyleR" />
    <Handle id="l" type="target" position="left" :style="sourceHandleStyleL" />

    <div
      v-if="
        data.type !== 'parallel' &&
          !data.is_in_parallel_flow &&
          sourceConnectionAvailable('b') &&
          (!sourceConnectionActive || (sourceConnectionActive.pos == 'b' && sourceConnectionActive.id == id))
      "
      :class="{ 'opacity-100 bg-gray-400': sourceConnectionActive?.id == id && sourceConnectionActive.pos == 'b' }"
      class="group-hover:opacity-100 hover:scale-150 cursor-pointer transition-all duration-500 ease-in-out opacity-0 hover:bg-gray-400 absolute -bottom-[10px] left-[calc(50%-10px)] w-5 h-5 border border-gray-400 rounded-full z-40"
      @click.stop="events.activateSourceConnection('b')" />

    <div
      v-if="targetConnectionAvailable('t') && !data.is_in_parallel_flow && sourceConnectionActive && sourceConnectionActive.id !== id"
      :class="{ 'opacity-100 bg-gray-400': targetConnectionActive?.id == id && sourceConnectionActive.pos == 't' }"
      class="group-hover:opacity-100 hover:scale-150 cursor-pointer transition-all duration-500 ease-in-out opacity-0 hover:bg-gray-400 absolute -top-[10px] left-[calc(50%-12px)] w-5 h-5 border border-gray-400 rounded-full z-40"
      @mouseenter="events.hoverOverTargetConnection('t')"
      @mouseleave="events.hoverAwayTargetConnection()"
      @click.stop="events.activateTargetConnection('t')" />

    <div v-if="sourceConnectionAvailable('b')" class="-bottom-6 h-10 w-10 absolute left-[calc(50%-11px)]" @click.stop>
      <div
        :class="[{ 'bg-[#2B3C44] scale-125 text-white': newNode?.id == id }]"
        class="bg-[#2B3C4415] hover:bg-[#2B3C44] mt-8 text-[#FFFFFF50] hover:text-white transition-all duration-500 ease-in-out hover:scale-125 absolute w-6 h-6 flex justify-center items-center text-center rounded-full z-40"
        @click="addNewNode(data, id, events.addNewItem)">
        <Plus class="w-6 h-6" />
      </div>
    </div>
    <slot name="body" />
  </div>
</template>

<script>
import { Handle } from "@vue-flow/core"
import { Plus, X } from "lucide-vue-next"

export default {
  components: { X, Handle, Plus },
  props: {
    newNode: null,
    addNewNode: null,
    position: null,
    elements: null,
    events: null,
    data: null,
    type: null,
    sourceConnectionActive: null,
    targetConnectionActive: null,
    id: null,
    getConnectedElements: null,
    getConnectedSourceElements: null
  },
  emits: [],
  data() {
    return {}
  },

  computed: {
    delete_available() {
      let delete_available_calc = true
      if (this.data?.is_trigger_group_merge) {
        delete_available_calc = false
      }

      if (this.type == "start") {
        delete_available_calc = false
      }

      if (this.data.type == "split") {
        const connected_elements_to_trigger_group_merges = this.getConnectedElements(this.id).filter(
          (el) => el.data.type == "merge" && el.data.is_trigger_group_merge
        )
        if (connected_elements_to_trigger_group_merges.length) {
          delete_available_calc = false
        }

        if (this.getConnectedSourceElements(this.id)[0].data.type == "switch") {
          delete_available_calc = false
        }

        if (this.getConnectedSourceElements(this.id)[0].data.type == "parallel") {
          delete_available_calc = false
        }
      }

      if (this.data.type == "parallel" && this.getConnectedElements(this.id).length) {
        delete_available_calc = false
      }

      if (this.data?.type == "for" && this.getConnectedElements(this.id).length) {
        delete_available_calc = false
      }

      if (this.data.type == "switch" && this.getConnectedElements(this.id).length) {
        delete_available_calc = false
      }

      if (this.data.type == "end_parallel") {
        delete_available_calc = false
      }

      if (this.data.type == "end_for_loop") {
        delete_available_calc = false
      }

      return delete_available_calc
    },
    sourceHandleStyleT() {
      return { backgroundColor: "transparent", border: 0, pointerEvents: "none", right: "50%", top: "0" }
    },
    sourceHandleStyleB() {
      return { backgroundColor: "transparent", border: 0, pointerEvents: "none", right: "50%", top: "80%" }
    },
    sourceHandleStyleR() {
      return { backgroundColor: "transparent", border: 0, pointerEvents: "none", top: "50%", right: "0" }
    },
    sourceHandleStyleL() {
      return { backgroundColor: "transparent", border: 0, pointerEvents: "none", top: "50%", left: "0" }
    }
  },
  watch: {},

  created() {},
  methods: {
    sourceConnectionAvailable(pos) {
      let source_connection_available_calc = true

      if (this.type == "start") {
        source_connection_available_calc = false
      }
      if (this.data.type == "add") {
        source_connection_available_calc = false
      }
      if (this.data.type == "operator") {
        source_connection_available_calc = false
      }
      if (this.data.type == "split") {
        const available_source_handles = []
        const connections = this.elements.filter((el) => el.source == this.id)
        connections.forEach((con) => {
          if (!available_source_handles.includes(con.sourceHandle)) {
            available_source_handles.push(con.sourceHandle)
          }
        })

        if (available_source_handles.includes("r") && available_source_handles.includes("l")) {
          if (pos == "l" || pos == "r") {
            source_connection_available_calc = false
          }
        }

        if (available_source_handles.includes("t") && available_source_handles.includes("b")) {
          const connected_elements_to_split = this.getConnectedElements(this.id)
          if (connected_elements_to_split[0]?.position.x > this.position.x) {
            if (pos == "l" || pos == "b") {
              source_connection_available_calc = false
            }
          } else {
            if (pos == "r" || pos == "b") {
              source_connection_available_calc = false
            }
          }
        }
      }

      if (this.data.type == "merge") {
        const connected_elements = this.getConnectedElements(this.id)
        if (connected_elements.length > 1) {
          source_connection_available_calc = false
        } else {
          const available_target_handles = []
          const source_connections = this.elements.filter((el) => el.target == this.id)
          source_connections.forEach((con) => {
            if (!available_target_handles.includes(con.targetHandle)) {
              available_target_handles.push(con.targetHandle)
            }
          })

          if (available_target_handles.includes("r") && available_target_handles.includes("l")) {
            if (pos == "l" || pos == "r") {
              source_connection_available_calc = false
            }
          }

          if (available_target_handles.includes("t") && available_target_handles.includes("b")) {
            const connected_elements_to_target = this.getConnectedSourceElements(this.id)
            if (connected_elements_to_target[0]?.position.x > this.position.x) {
              if (pos == "r" || pos == "b") {
                source_connection_available_calc = false
              }
            } else {
              if (pos == "l" || pos == "b") {
                source_connection_available_calc = false
              }
            }
          }
        }
      }

      if (
        this.data.type !== "switch" &&
        this.data.type !== "split" &&
        !this.data.is_in_parallel_flow &&
        !this.data.node_in_loop &&
        this.data.type !== "parallel"
      ) {
        const elements_connected = this.getConnectedElements(this.id)
        if (elements_connected.length) {
          source_connection_available_calc = false
        }
      }

      if (this.data?.type == "for" && (this.getConnectedElements(this.id).length || !this.data.values.records)) {
        source_connection_available_calc = false
      }

      const connected_element = this.getConnectedElements(this.id, pos, true)[0]
      if (connected_element?.data?.type == "split") {
        source_connection_available_calc = false
      }

      return source_connection_available_calc
    },

    targetConnectionAvailable(pos) {
      let target_connection_available_calc = true
      if (this.type == "start") {
        target_connection_available_calc = false
      }
      if (this.data.type == "add") {
        target_connection_available_calc = false
      }

      if (this.data.type == "operator") {
        target_connection_available_calc = false
      }
      if (this.data.type == "split") {
        const connected_source_elements = this.getConnectedSourceElements(this.id)
        if (connected_source_elements[0]?.data?.type == "merge") {
          target_connection_available_calc = false
        } else {
          const source_links = this.elements.filter((el) => el.source == this.id)
          const available_source_handles = []

          source_links.forEach((link) => {
            if (!available_source_handles.includes(link.sourceHandle)) {
              available_source_handles.push(link.sourceHandle)
            }
            if (available_source_handles.includes("l") && available_source_handles.includes("r")) {
              if (pos == "l" || pos == "r") {
                target_connection_available_calc = false
              }
            }
            if (available_source_handles.includes("t") && available_source_handles.includes("b")) {
              const connected_elements_to_source = this.getConnectedElements(this.id)
              if (connected_elements_to_source[0]?.position.x < this.position.x) {
                if (pos == "t" || pos == "l") {
                  target_connection_available_calc = false
                }
              } else {
                if (pos == "t" || pos == "r") {
                  target_connection_available_calc = false
                }
              }
            }
          })
        }
      }

      if (this.data.type == "merge") {
        if (this.data.is_trigger_group_merge) {
          return false
        }

        const target_links = this.elements.filter((el) => el.target == this.id)
        const available_target_handles = []

        target_links.forEach((link) => {
          if (!available_target_handles.includes(link.targetHandle)) {
            available_target_handles.push(link.targetHandle)
          }
          if (available_target_handles.includes("l") && available_target_handles.includes("r")) {
            if (pos == "l" || pos == "r") {
              target_connection_available_calc = false
            }
          }
          if (available_target_handles.includes("t") && available_target_handles.includes("b")) {
            const connected_elements_to_target = this.getConnectedSourceElements(this.id)
            if (connected_elements_to_target[0]?.position.x < this.position.x) {
              if (pos == "r" || pos == "t") {
                target_connection_available_calc = false
              }
            } else {
              if (pos == "l" || pos == "t") {
                target_connection_available_calc = false
              }
            }
          }
        })
      }

      if (this.sourceConnectionActive?.data?.type == "trigger") {
        if (this.sourceConnectionActive?.pos == "r") {
          if (pos !== "l") {
            target_connection_available_calc = false
          } else if (this.data.type !== "trigger") {
            target_connection_available_calc = false
          }
        }
        if (this.sourceConnectionActive?.pos == "b") {
          if (this.data.type == "trigger" && (pos == "l" || pos == "r")) {
            target_connection_available_calc = false
          }
        }
      }

      if (this.sourceConnectionActive?.data?.type !== "trigger" && this.data.type == "trigger") {
        if (this.sourceConnectionActive?.pos == "r" || this.sourceConnectionActive?.pos == "l") {
          target_connection_available_calc = false
        }
        if (this.sourceConnectionActive?.pos == "b" && (pos == "l" || pos == "r")) {
          target_connection_available_calc = false
        }
      }

      const connected_element = this.getConnectedSourceElements(this.id, pos)[0]
      if (connected_element?.data?.type == "merge") {
        target_connection_available_calc = false
      }

      return target_connection_available_calc
    }
  }
}
</script>
<style scoped>
.handle-alt {
  background-color: rgba(0, 0, 0, 0);
  pointer-events: none;

  border: 0;
  position: absolute;
}
</style>
